import React from 'react';
import {
	Menu,
	MenuItem,
	Fade,
	ListSubheader,
	Checkbox,
	ListItemText,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as UserSelectors from 'selectors/UserSelectors';

interface Props {
	open: boolean;
	value: string[];
	onChange: (value: string[]) => void;
	onClose: () => void;
	anchorEl?: HTMLElement | null;
}

const LocationsFilter = (props: Props) => {
	const { open, value, onChange, onClose, anchorEl } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const userLocations = useSelector(UserSelectors.userLocations);
	const userLocationIds = userLocations.map((l) => l.id);
	const allOptions = [...userLocationIds, 'ALL'];
	const isAllSelected = value.length === allOptions.length;

	const handleLocationSelect = (locationId: string) => {
		if (value.includes(locationId)) {
			onChange(value.filter((id) => id !== locationId));
		} else {
			onChange([...value, locationId]);
		}
	};

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			keepMounted
			TransitionComponent={Fade}
			classes={{
				paper: classes.menuPaper,
			}}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'left',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'left',
			}}
		>
			<MenuItem onClick={() => onChange(!isAllSelected ? allOptions : [])}>
				<Checkbox color="primary" checked={isAllSelected} className={classes.checkboxAll} />
				<ListItemText primary={t('common:count.all')} />
			</MenuItem>

			<ListSubheader className={classes.subHeader}>
				<Typography variant="caption">{t('common:shopLocation.stores')}</Typography>
			</ListSubheader>
			<MenuItem key="ALL" onClick={() => handleLocationSelect('ALL')}>
				<Checkbox
					color="primary"
					checked={Array.isArray(value) && value.includes('ALL')}
					className={classes.checkbox}
				/>
				<ListItemText
					primary={t('common:shopLocation.allStores')}
					secondary={t('users.allStoresDescription', 'Users with access to all stores')}
				/>
			</MenuItem>

			{userLocations.map((location) => (
				<MenuItem key={location.id} onClick={() => handleLocationSelect(location.id)}>
					<Checkbox
						color="primary"
						checked={Array.isArray(value) && value.includes(location.id)}
						className={classes.checkbox}
					/>
					<ListItemText primary={location.name} />
				</MenuItem>
			))}
		</Menu>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		menuPaper: {
			minWidth: 250,
		},
		subHeader: {
			textTransform: 'uppercase',
			lineHeight: '32px',
		},
		checkboxAll: {
			marginLeft: theme.spacing(-1.5),
		},
		checkbox: {
			marginLeft: theme.spacing(0.75),
		},
		resetFilters: {
			color: theme.palette.text.secondary,
		},
	}),
);

export default LocationsFilter;
